import { z } from "zod";
import { Action, Subject } from "@erp/authz";
import { SystemEntity, SystemEvent } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { SystemEntitySchema } from "../../utils/system-entities";
import { taskOutputSchema } from "./shared";

/**
 * Generate Task Step
 *
 * AI-powered task generation from workflow execution context and config instructions.
 * Synthesizes task fields using an LLM, then creates the task.
 * Used as both an agent tool (via ToolCatalog) and a workflow step (via StepRegistry).
 */
export const generateTaskStep = defineStepConfig({
  type: "generate_task",
  name: "Generate Task",
  description:
    "Generate and create a task using AI to synthesize fields from workflow context and instructions",
  category: "Tasks",

  inputSchema: z
    .object({
      sourceEntity: SystemEntitySchema.optional().describe(
        "Entity to link to this task via TASK_EXECUTION association. When the selected task type declares an anchor entity type, this entity is also used as the task's anchor if it matches (and is required unless `anchorEntity` is wired).",
      ),
      anchorEntity: SystemEntitySchema.optional().describe(
        "Business entity to anchor the generated task to (requires a task type declaring a matching anchorEntityType). When the type declares an anchor, `sourceEntity` may be used instead if it matches. Fails if another open task already holds the (type, anchor) slot.",
      ),
      input: z
        .record(z.string(), z.unknown())
        .optional()
        .describe(
          "Typed payload required when the selected task type declares an input schema. Field sockets `taskInput.<name>` overlay this bag.",
        ),
    })
    .catchall(z.unknown()),

  outputSchema: z.object({
    task: taskOutputSchema,
  }),

  configSchema: z.object({
    instructions: z
      .string()
      .describe(
        "Instructions for the AI on how to generate the task (e.g., 'Generate a task to run purchase matching on the invoice passed in.')",
      ),
    title: z
      .string()
      .optional()
      .describe("Optional fixed title to use instead of AI-generated title"),
    priority: z
      .enum(["low", "medium", "high", "urgent"])
      .optional()
      .describe(
        "Optional fixed priority to use instead of AI-generated priority",
      ),
    assigneeType: z
      .enum(["user", "agent"])
      .optional()
      .describe("Type of assignee for generated tasks"),
    assigneeUserId: z
      .uuid()
      .optional()
      .describe("User ID when assigneeType is user"),
    assigneeAgentDefinitionId: z
      .uuid()
      .optional()
      .describe("Agent definition assignee ID when assigneeType is agent"),
    taskNamespaceKey: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe(
        "Namespace key (e.g. 'SHIP') to create generated tasks in. Portable across orgs — resolved to the namespace at run time.",
      ),
    taskNamespaceId: z
      .uuid()
      .optional()
      .describe(
        "Namespace id to create generated tasks in. Prefer `taskNamespaceKey` in templates.",
      ),
    taskTypeKey: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe(
        "Org-scoped task type key giving generated tasks typed behavior (anchoring, outcomes, completion gating).",
      ),
    taskTypeAnchorEntityType: z
      .nativeEnum(SystemEntity)
      .optional()
      .describe(
        "Copied from the selected task type's declared anchor entity. Drives the sourceEntity input socket (required + typed) so the canvas enforces the type's source-entity constraint. Set by the task-type picker; do not set independently of taskTypeKey.",
      ),
    taskTypeInputFields: z
      .array(
        z.object({
          fieldName: z.string(),
          fieldType: z.string(),
          label: z.string(),
          isRequired: z.boolean(),
          isMultiple: z.boolean(),
        }),
      )
      .optional()
      .describe(
        "Copied from the selected task type's input schema. Drives per-field taskInput.* sockets. Set by the task-type picker; do not set independently of taskTypeKey.",
      ),
  }),

  requiredPermissions: [{ action: Action.Create, subject: Subject.Task }],

  emits: [SystemEvent.TASK_CREATED] as const,
});
